import PropTypes from 'prop-types';
import React from 'react';
import SectionContent from '../SectionContent';
import styled from 'styled-components';
import VisibilitySensor from 'react-visibility-sensor';

const StyledSection = styled.div`
  align-items: flex-end;
  background-position: center center;
  background-repeat: no-repeat;
  background-size: cover;
  display: flex;
  font-family: 'acumin-pro', sans-serif;
  height: calc((var(--vh, 1vh) * 100) - 76px);
  justify-content: center;
  margin: 0 auto;
  max-width: 1900px;
  opacity: ${(props) => (props.visible ? '1' : '0.7')};
  overflow: hidden;
  position: relative;
  transition: all 600ms ease-in-out;
  width: 100%;

  @media (min-width: 800px), (min-device-width: 768px) {
    justify-content: flex-start;
    height: calc(100vh - 90px);
  }

  ::before {
    background: linear-gradient(
      to bottom,
      rgba(255, 255, 255, 0),
      rgba(0, 0, 0, 0.35)
    );
    bottom: 0;
    content: '';
    display: block;
    left: 0;
    position: absolute;
    right: 0;
    top: 0;
    z-index: 2;
    pointer-events: none;
  }
`;

export const BgVideo = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  display: ${(props) => (props.hasMobileVideo ? 'none' : 'block')};

  video {
    height: 100%;
    width: ${(props) => (props.offsetBg ? '110%' : '100%')};
    object-fit: cover;
    object-position: center center;
    position: absolute;

    /* Video cover fix for IE11 */
    @media screen and (-ms-high-contrast: active), (-ms-high-contrast: none) {
      height: auto;
      left: 50%;
      min-height: 100%;
      min-width: 100%;
      position: absolute;
      top: 50%;
      transform: translate(-50%, -50%);
      width: auto;
    }
    /* Video cover fix for Edge */
    @supports (-ms-ime-align: auto) {
      height: auto;
      left: 50%;
      min-height: 100%;
      min-width: 100%;
      position: absolute;
      top: 50%;
      transform: translate(-50%, -50%);
      width: auto;
    }
  }

  @media (min-width: 800px), (min-device-width: 768px) {
    display: block;
  }
`;

export const MobileBgVideo = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;

  video {
    height: 100%;
    width: ${(props) => (props.offsetBg ? '110%' : '100%')};
    object-fit: cover;
    object-position: center center;
    position: absolute;

    /* Video cover fix for IE11 */
    @media screen and (-ms-high-contrast: active), (-ms-high-contrast: none) {
      height: auto;
      left: 50%;
      min-height: 100%;
      min-width: 100%;
      position: absolute;
      top: 50%;
      transform: translate(-50%, -50%);
      width: auto;
    }
    /* Video cover fix for Edge */
    @supports (-ms-ime-align: auto) {
      height: auto;
      left: 50%;
      min-height: 100%;
      min-width: 100%;
      position: absolute;
      top: 50%;
      transform: translate(-50%, -50%);
      width: auto;
    }
  }

  @media (min-width: 800px), (min-device-width: 768px) {
    display: none;
  }
`;

export const BgImage = styled.img`
  height: 100%;
  left: 0;
  object-fit: cover;
  object-position: center center;
  position: absolute;
  top: 0;
  width: 100%;

  /* Image cover fix for IE */
  @media screen and (-ms-high-contrast: active), (-ms-high-contrast: none) {
    height: auto;
  }
`;

class Section extends React.Component {
  constructor() {
    super();

    this.state = {
      isVisible: false,
    };

    this.videoRef = null;

    this.setVideoRef = (element) => {
      this.videoRef = element;
    };

    this.visibilityChange = this.visibilityChange.bind(this);
  }

  componentDidMount() {
    const video =
      this.videoRef && this.videoRef.getElementsByTagName('video')[0];
    video && video.play();

    const vh = window.innerHeight * 0.01;
    document.getElementById('dtr-intro').style.setProperty('--vh', `${vh}px`);
  }

  visibilityChange(isVisible) {
    this.setState({
      isVisible: isVisible,
    });
  }

  render() {
    return (
      <VisibilitySensor
        partialVisibility={true}
        minTopValue={300}
        onChange={this.visibilityChange}
        active={!this.state.isVisible}
      >
        <StyledSection visible={this.state.isVisible ? true : false}>
          <SectionContent
            header={this.props.header}
            blurb={this.props.blurb}
            cta={this.props.cta}
            gaLabel={this.props.gaLabel}
            smallScreen={this.props.smallScreen}
          />

          {this.props.children}

          {this.props.bgVideo && (
            <BgVideo
              innerRef={this.setVideoRef}
              hasMobileVideo={!!this.props.mobileBgVideo}
              dangerouslySetInnerHTML={{
                __html: `
                        <video
                           src="${this.props.bgVideo}"
                           poster="${this.props.poster}"
                           autoplay
                           muted
                           loop
                           playsinline
                        />
                     `,
              }}
            />
          )}

          {this.props.mobileBgVideo && (
            <MobileBgVideo
              offsetBg={this.props.offsetBg}
              dangerouslySetInnerHTML={{
                __html: `
                     <video
                        src="${this.props.mobileBgVideo}"
                        poster="${
                          this.props.posterMobile
                            ? this.props.posterMobile
                            : this.props.poster
                        }"
                        autoplay
                        muted
                        loop
                        playsinline
                     />
                  `,
              }}
            />
          )}

          {this.props.bgImage && (
            <BgImage
              offsetBg={this.props.offsetBg}
              src={this.props.bgImage}
              alt="background"
            />
          )}
        </StyledSection>
      </VisibilitySensor>
    );
  }
}

Section.propTypes = {
  bgImage: PropTypes.any,
  bgVideo: PropTypes.any,
  blurb: PropTypes.any,
  children: PropTypes.any,
  cta: PropTypes.any,
  gaLabel: PropTypes.any,
  header: PropTypes.any,
  mobileBgVideo: PropTypes.any,
  offsetBg: PropTypes.any,
  poster: PropTypes.any,
  posterMobile: PropTypes.any,
  smallScreen: PropTypes.any,
};

export default Section;
